import { Component, Input, Output, EventEmitter, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { CommonConfirmationModalData } from
  'src/app/components/common/common-confirmation-modal/common-confirmation-modal.component.model';
import { SecurityService, WorkflowContextService } from 'src/app/services';
import { ToastrService } from 'ngx-toastr';
import { svgs } from 'src/assets/export.assets';

interface JanusPermission {
  id: string;
  name: string;
}

interface RoleMember {
  id: string;
  name: string;
  email: string;
}

@Component({
  selector: 'wm-role-details-janus',
  standalone: false,
  templateUrl: './role-details-janus.component.html',
  styleUrls: ['./role-details-janus.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class RoleDetailsJanusComponent implements OnInit {

  /* =======================
     INPUTS / OUTPUTS
  ======================= */
  @Input() role!: {
    name: string;
    color: string;
    permissions: string[];
    members: RoleMember[];
  };

  @Input() allPermissions: JanusPermission[] = [];

  @Output() removeUser = new EventEmitter<string>();
  @Output() save = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();
  @Output() delete = new EventEmitter<void>();


  /* =======================
     STATE
  ======================= */
  isPermissionsOpen = true;
  deleteIcon = svgs.deleteOutlineIcon.name;
  colorOptions = [
    { name: 'Green', value: 'green' },
    { name: 'Yellow', value: 'yellow' },
    { name: 'Orange', value: 'orange' },
    { name: 'Red', value: 'red' },
    { name: 'Purple', value: 'purple' },
    { name: 'Light Blue', value: 'light-blue' },
    { name: 'Light Green', value: 'light-green' },
    { name: 'Light Yellow', value: 'light-yellow' }
  ];

  memberCtrl = new FormControl('');
  memberSearchResults: RoleMember[] = [];
  selectedMember: RoleMember | null = null;
  occupiedWorkflows: string[] = [];
  adminWorkflows: string[] = [];
  isDeleteInfoOpen = true;
  canDelete = false;
  loadingDeleteInfo = true;


  private pendingRemoveId: string | null = null;

  /* =======================
     MODAL STATE
  ======================= */
  confirmationModal: CommonConfirmationModalData = this.getResetModal();
  activeModalAction: 'removeUser' | 'deleteRole' | null = null;

  constructor(
    private securitySvc: SecurityService,
    private context: WorkflowContextService,
    private toastr: ToastrService
  ) { }

  ngOnInit(): void {
    this.memberCtrl.valueChanges
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe(query => {
        if (!query || typeof query !== 'string') {
          this.memberSearchResults = [];
          return;
        }

        this.securitySvc
          .searchUsers(this.context.client, { searchText: query })
          .subscribe((users: any[]) => {
            this.memberSearchResults = users.map(u => ({
              id: u.id,
              name: u.name,
              email: u.emailAddress
            }));
          });
      });
    this.loadDeleteInfo();

  }
  private loadDeleteInfo(): void {
    this.loadingDeleteInfo = true;

    this.securitySvc.canDeleteRole(this.role as any).subscribe(res => {
      const errorMessages = res.errorMessages || [];
      const workflowAdmins = res.workflowAdmins || [];

      // 1️⃣ Extract rule message (ALWAYS LAST)
      const ruleMessages = errorMessages.filter(m =>
        m.toLowerCase().includes('role is defined')
      );

      // 2️⃣ Extract workflow messages from errorMessages
      const workflowErrors = errorMessages.filter(m =>
        m.toLowerCase().startsWith('workflow:')
      );

      // 3️⃣ Normalize workflowAdmins → "Workflow: X"
      const workflowAdminMessages = workflowAdmins.map(
        w => `Workflow: ${w}`
      );

      // 4️⃣ Deduplicate workflows
      const uniqueWorkflows = Array.from(
        new Set([...workflowErrors, ...workflowAdminMessages])
      );

      // 5️⃣ Final ordered list (matches legacy UI)
      this.occupiedWorkflows = [
        ...uniqueWorkflows,
        ...ruleMessages
      ];

      // 6️⃣ Delete allowed ONLY if nothing blocks it
      this.canDelete = this.occupiedWorkflows.length === 0;

      this.loadingDeleteInfo = false;
    });
  }

  openDeleteConfirm(): void {
    this.activeModalAction = 'deleteRole';
    this.confirmationModal = {
      open: true,
      modalDescp: 'Delete Role',
      modalSubDescp: `Are you sure you want to disable this role (${this.role.name})?`,
      yesNoBtn: true,
      okBtn: false,
      closeBtn: false,
      status: 'default'
    };
  }

  onConfirmDelete(): void {
    this.confirmationModal = this.getResetModal();
    this.delete.emit(); // 🔥 parent handles API
  }


  closeDeleteModal(): void {
    this.confirmationModal = this.getResetModal();
  }


  toggleDeleteInfo(): void {
    this.isDeleteInfoOpen = !this.isDeleteInfoOpen;
  }

  /* =======================
     MODAL RESET (CRITICAL)
  ======================= */
  private getResetModal(): CommonConfirmationModalData {
    return {
      open: false,
      modalDescp: '',
      modalSubDescp: '',
      yesNoBtn: false,
      okBtn: false,
      closeBtn: false,
      status: 'default'
    };
  }

  private openModal(
    message: string,
    options?: Partial<CommonConfirmationModalData>
  ): void {
    this.confirmationModal = {
      ...this.getResetModal(),
      open: true,
      modalDescp: 'Roles',        // ✅ FIXED HEADER
      modalSubDescp: message,     // ✅ ACTUAL MESSAGE
      ...options
    };
  }


  closeModal(): void {
    this.activeModalAction = null; 
    this.pendingRemoveId = null;   
    this.confirmationModal = this.getResetModal();
  }

  /* =======================
     DROPDOWN
  ======================= */
  onMemberSelected(user: RoleMember): void {
    this.selectedMember = user;
  }

  displayMember = (member: any): string =>
    member ? member.name : '';

  /* =======================
     ADD MEMBER (FULLY FIXED)
  ======================= */
  addMember(): void {
    if (!this.selectedMember) { return; }

    const exists = this.role.members.some(
      m => m.id === this.selectedMember!.id
    );

    if (exists) {
      this.openModal(
        'User already exists in this role.',
        {
          okBtn: true,
          status: 'error'
        }
      );
      return;
    }


    const payload = { id: this.selectedMember.id };

    this.securitySvc
      .associateRole(payload as any, this.role)
      .subscribe({
        next: () => {
          this.role.members.push({ ...this.selectedMember! });

          this.selectedMember = null;
          this.memberCtrl.setValue('');
          this.memberSearchResults = [];

          this.openModal(
            'Member added successfully.',
            {
              okBtn: true,
              status: 'success'
            }
          );

        },
        error: () => {
          this.openModal(
            'Failed to add member.',
            {
              okBtn: true,
              status: 'error'
            }
          );

        }
      });
  }

  /* =======================
     REMOVE MEMBER (CONFIRM)
  ======================= */
  removeMember(id: string): void {
    this.pendingRemoveId = id;
    this.activeModalAction = 'removeUser';

    this.openModal(
      'Are you sure you want to remove this user from the role?',
      {
        yesNoBtn: true,
        status: 'info'
      }
    );
  }

  handleModalConfirm(): void {
    if (this.activeModalAction === 'removeUser') {
      this.onConfirmRemove();
    } else if (this.activeModalAction === 'deleteRole') {
      this.onConfirmDelete();
    }
  }


  onConfirmRemove(): void {
    if (!this.pendingRemoveId) { return; }

    this.removeUser.emit(this.pendingRemoveId);
    this.pendingRemoveId = null;
    this.activeModalAction = null;
    this.closeModal();
    this.toastr.success('Member removed successfully.');
    /*this.openModal(
      'Member removed successfully.',
      {
        okBtn: true,
        yesNoBtn: false,
        status: 'success'
      }
    );*/

  }

  /* =======================
     PERMISSIONS
  ======================= */
  togglePermission(id: string): void {
    this.role.permissions = this.role.permissions.includes(id)
      ? this.role.permissions.filter(p => p !== id)
      : [...this.role.permissions, id];
  }

  toggleAllPermissions(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.role.permissions = checked
      ? this.allPermissions.map(p => p.id)
      : [];
  }

  isChecked(id: string): boolean {
    return this.role.permissions.includes(id);
  }

  togglePermissions(): void {
    this.isPermissionsOpen = !this.isPermissionsOpen;
  }
}
